import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { current: "", next: "", confirm: "" };

export const ChangePasswordDialog = ({ open, onOpenChange, forced = false }) => {
  const { setUser } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.next.length < 8) return setError("New password must be at least 8 characters.");
    if (f.next !== f.confirm) return setError("New passwords don't match.");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      setUser(data);
      setF(EMPTY);
      toast.success("Password changed. Other devices have been signed out.");
      onOpenChange?.(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, testid, auto) => (
    <div className="space-y-1.5">
      <Label htmlFor={testid}>{label}</Label>
      <Input id={testid} data-testid={testid} type="password" autoComplete={auto} required
        value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={forced ? undefined : onOpenChange}>
      <DialogContent data-testid="change-password-dialog" className="sm:max-w-md"
        onInteractOutside={(e) => forced && e.preventDefault()} onEscapeKeyDown={(e) => forced && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">{forced ? "Set your own password" : "Change password"}</DialogTitle>
          <DialogDescription>
            {forced ? "You signed in with a temporary password. Choose a personal password only you know to continue."
              : "At least 8 characters. Changing it signs this account out on all other devices."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {field("current", forced ? "Temporary password" : "Current password", "cp-current-input", "current-password")}
          {field("next", "New password", "cp-new-input", "new-password")}
          {field("confirm", "Confirm new password", "cp-confirm-input", "new-password")}
          {error && <div data-testid="cp-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <Button data-testid="cp-submit-button" type="submit" disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? "Saving…" : "Save new password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
